import React, { useState, useRef, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { Box, Typography, IconButton, CircularProgress } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { toast } from "react-toastify";
import { base_url } from "../../utils/api";
import { getUser } from "../../utils/constants";

const EditAttachmentModal = ({
  show,
  handleClose,
  documentData,
  onUploadSuccess,
}) => {
  const user = JSON.parse(getUser());
  const [isDragging, setIsDragging] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(true);
  const fileInputRef = useRef(null);
  const getFileTypeFromUrl = (url) => {
    if (!url) return null;
    const ext = url.split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png", "webp"].includes(ext)) return "image";
    if (ext === "pdf") return "pdf";
    if (["doc", "docx"].includes(ext)) return "doc";
    return "unknown";
  };

  // ✅ Load existing image preview when modal opens
  useEffect(() => {
    if (show && documentData?.document_url) {
      setPreviewLoading(true);
      const fileType = getFileTypeFromUrl(documentData.document_url);
      if (fileType === "image") {
        setImagePreview(documentData.document_url);
      } else if (fileType === "pdf" || fileType === "doc") {
        setImagePreview({
          type: fileType,
          url: documentData.document_url,
          name:
            documentData.document_name ||
            documentData.document_url.split("/").pop(),
        });
        setPreviewLoading(false);
      } else {
        setImagePreview(null);
        setPreviewLoading(false);
      }
    } else {
      setImagePreview(null);
      setPreviewLoading(false);
    }
  }, [show, documentData]);

  const handleFileChange = (selectedFile) => {
    if (!selectedFile) return;

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/jpg",
      "application/pdf",
    ];

    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error("Only JPG, PNG, PDF files are allowed!", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    setFile(selectedFile);
    setPreviewLoading(true);

    if (selectedFile.type.startsWith("image/")) {
      setImagePreview(URL.createObjectURL(selectedFile));
    } else if (selectedFile.type === "application/pdf") {
      setImagePreview({ type: "pdf", name: selectedFile.name });
      setPreviewLoading(false);
    } else {
      setImagePreview({ type: "doc", name: selectedFile.name });
      setPreviewLoading(false);
    }
  };

  const handleRemoveImage = () => {
    setFile(null);
    setImagePreview(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    handleFileChange(droppedFile);
  };

  const handleUpload = async () => {
    try {
      setLoading(true);

      // ✅ Case 1: No new file selected → don't upload, just close modal
      if (!file) {
        toast.info("No new document selected. Keeping existing file.", {
          position: toast.POSITION.TOP_RIGHT,
        });
        handleClose();
        return;
      }

      // ✅ Case 2: New file selected → upload it
      const formData = new FormData();
      formData.append("document_url", file);

      const response = await axios.post(
        `${base_url}admin/updateDocument?document_id=${documentData?.document_id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${user?.authToken}`,
          },
        }
      );

      toast.success("Document updated successfully", {
        position: toast.POSITION.TOP_RIGHT,
        className: "bg-success text-white",
      });

      setFile(null);
      setImagePreview(null);
      handleClose();
      if (onUploadSuccess) onUploadSuccess();
    } catch (error) {
      console.error("Error uploading document:", error);
      toast.error("Failed to upload document ❌", {
        position: toast.POSITION.TOP_RIGHT,
        className: "bg-danger text-white",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} centered onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Upload Document</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          sx={{
            border: "2px dashed #ccc",
            borderRadius: "8px",
            padding: "25px",
            textAlign: "center",
            cursor: "pointer",
            backgroundColor: isDragging ? "#f5f5f5" : "white",
            transition: "border-color 0.3s ease",
            "&:hover": {
              borderColor: "#888",
            },
          }}
        >
          <label htmlFor="image" style={{ cursor: "pointer" }}>
            <input
              id="image"
              type="file"
              accept="image/jpeg,image/png,image/jpg,application/pdf"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={(event) => {
                const selectedFile = event.currentTarget.files?.[0] || null;
                handleFileChange(selectedFile);
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CloudUploadIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="body1">
                {isDragging
                  ? "Drop the file here"
                  : "Click or drag an image or PDF to upload"}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                (Allowed: JPG, JPEG, PNG, PDF)
              </Typography>
            </Box>
          </label>
        </Box>

        {imagePreview && (
          <Box
            sx={{
              mt: 2,
              position: "relative",
              display: "inline-block",
            }}
          >
            {/* IMAGE PREVIEW */}
            {typeof imagePreview === "string" ? (
              <Box
                sx={{
                  width: "200px",
                  height: "200px",
                  // border: "1px solid #ccc",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  // backgroundColor: "#f9f9f9",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                {/* Loader */}
                {previewLoading && (
                  <CircularProgress
                    size={40}
                    sx={{
                      position: "absolute",
                      color: "#1976d2",
                    }}
                  />
                )}

                <img
                  src={imagePreview}
                  alt="Preview"
                  onLoad={() => setPreviewLoading(false)}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    display: previewLoading ? "none" : "block",
                  }}
                />
              </Box>
            ) : (
              // DOCUMENT PREVIEW (PDF/DOC)
              <Box
                sx={{
                  width: "200px",
                  height: "200px",
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f9f9f9",
                  p: 2,
                  textAlign: "center",
                }}
              >
                {imagePreview.type === "pdf" ? (
                  <InsertDriveFileIcon
                    sx={{ color: "#8A2BE2", fontSize: 45 }}
                  />
                ) : (
                  <Typography variant="h3" sx={{ color: "#007bff", mb: 1 }}>
                    📄
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  sx={{
                    wordBreak: "break-all",
                    fontWeight: 500,
                    color: "#333",
                  }}
                >
                  {imagePreview.name}
                </Typography>

                {/* ✅ View Existing PDF/DOC Button */}
                {/* {imagePreview.url && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="mt-2"
                    onClick={() => window.open(imagePreview.url, "_blank")}
                  >
                    View File
                  </Button>
                )} */}
              </Box>
            )}

            {/* Remove Button */}
            {!previewLoading && (
              <IconButton
                size="small"
                onClick={handleRemoveImage}
                sx={{
                  position: "absolute",
                  top: -10,
                  right: -10,
                  backgroundColor: "white",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                  "&:hover": { backgroundColor: "grey.100" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        )}
      </Modal.Body>

      <Modal.Footer className="text-end gap-1 border-0 px-3 py-2">
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="danger" onClick={handleUpload} disabled={loading}>
          {loading ? (
            <>
              Uploading...
              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </>
          ) : (
            "Upload"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditAttachmentModal;
